import {
  ALL_BRANCHES,
  filterTransactionsInRange,
  getGroupsForBranch,
  getMembersForBranch,
  getTransactionsForBranch
} from './generator';

type DateField = 'due_at' | 'paid_at';
type MockTransaction = ReturnType<typeof getTransactionsForBranch>[number];

const parseDMY = (value: string) => {
  const [d, m, y] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toDateKey = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const sumPaid = (transactions: MockTransaction[]) =>
  transactions.reduce((sum, t) => sum + Number(t.total_paid_amount), 0);

const sumDue = (transactions: MockTransaction[]) =>
  transactions
    .filter((t) => t.status === 'processing')
    .reduce(
      (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
      0
    );

const sumUnsettled = (transactions: MockTransaction[]) =>
  transactions
    .filter((t) => t.status === 'success' && t.settlement_status !== 'settled')
    .reduce((sum, t) => sum + Number(t.total_paid_amount), 0);

const getGrowthPercentage = (current: number, previous: number) => {
  if (previous > 0) {
    return Math.round(((current - previous) / previous) * 100);
  }
  return current > 0 ? 100 : 0;
};

export const getPaymentOverviewMock = (
  timeFilterType: string,
  startDate: string,
  endDate: string,
  branchId?: string
) => {
  const dateField: DateField =
    timeFilterType === 'paid_at' ? 'paid_at' : 'due_at';
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);

  const inRange = filterTransactionsInRange(branchId, start, end, dateField);

  const periodMs = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 86400000);
  const prevStart = new Date(prevEnd.getTime() - periodMs);
  const prevRange = filterTransactionsInRange(
    branchId,
    prevStart,
    prevEnd,
    dateField
  );

  const collectionAmount = sumPaid(
    inRange.filter((t) => t.status === 'success')
  );
  const prevCollectionAmount = sumPaid(
    prevRange.filter((t) => t.status === 'success')
  );
  const collectionGrowth = getGrowthPercentage(
    collectionAmount,
    prevCollectionAmount
  );

  return {
    collection_amount: collectionAmount,
    collection_growth: collectionGrowth,
    due_amount: sumDue(inRange),
    to_be_settled: sumUnsettled(inRange),
    groups: getGroupsForBranch(branchId).length,
    members: getMembersForBranch(branchId).length
  };
};

export const getBranchRevenueMock = (
  startDate: string,
  endDate: string,
  branchIdsParam?: string
) => {
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);
  const requestedIds = branchIdsParam
    ? branchIdsParam.split(',').filter(Boolean)
    : [];
  const branches = requestedIds.length
    ? ALL_BRANCHES.filter((b) => requestedIds.includes(b.id))
    : ALL_BRANCHES;

  return branches.map((branch) => {
    const branchTransactions = getTransactionsForBranch(branch.id);
    const revenue: { date: string; collected: number; due: number }[] = [];

    const current = new Date(start);
    while (current <= end) {
      const dateKey = toDateKey(current);
      const collected = branchTransactions
        .filter(
          (t: any) =>
            t.status === 'success' &&
            t.paid_at &&
            toDateKey(new Date(t.paid_at)) === dateKey
        )
        .reduce((sum, t) => sum + Number(t.total_paid_amount), 0);
      const due = branchTransactions
        .filter(
          (t: any) =>
            t.status === 'processing' &&
            toDateKey(new Date(t.due_at)) === dateKey
        )
        .reduce(
          (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
          0
        );
      revenue.push({ date: dateKey, collected, due });
      current.setDate(current.getDate() + 1);
    }

    return {
      branch_id: branch.id,
      branch_name: branch.name,
      branch_revenue: revenue
    };
  });
};

export const getGroupPerformanceMock = (
  page: number,
  timeFilterType: string,
  startDate: string,
  endDate: string,
  branchId?: string
) => {
  const dateField: DateField =
    timeFilterType === 'paid_at' ? 'paid_at' : 'due_at';
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);
  const groups = getGroupsForBranch(branchId);

  const allGroups = groups.map((group) => {
    const groupTransactions = filterTransactionsInRange(
      group.branch_id,
      start,
      end,
      dateField
    ).filter((t) => t.group_id === group.id);

    return {
      group_id: group.id,
      group_name: group.name,
      branch_id: group.branch_id,
      branch_name: group.branch_name,
      amount_collected: String(
        sumPaid(groupTransactions.filter((t) => t.status === 'success'))
      ),
      amount_due: String(sumDue(groupTransactions))
    };
  });

  const limit = 5;
  const pageStart = page * limit;
  const paginatedData = allGroups.slice(pageStart, pageStart + limit);

  return {
    data: paginatedData.length > 0 ? paginatedData : null,
    pagination: {
      total_rows: allGroups.length,
      page,
      limit
    }
  };
};

export const getPaymentLinksAndModesMock = (
  timeFilterType: string,
  startDate: string,
  endDate: string,
  branchId?: string
) => {
  const dateField: DateField =
    timeFilterType === 'paid_at' ? 'paid_at' : 'due_at';
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);
  const inRange = filterTransactionsInRange(branchId, start, end, dateField);

  const modeTotals = {
    upi: 0,
    cards: 0,
    net_banking: 0,
    online_mandates: 0,
    others: 0,
    offline: 0
  };

  inRange
    .filter((t) => t.status === 'success')
    .forEach((t) => {
      const amount = Number(t.total_paid_amount);
      if (t.payment_method === 'cards') {
        modeTotals.cards += amount;
      } else if (t.payment_method === 'net_banking') {
        modeTotals.net_banking += amount;
      } else if (t.payment_method === 'offline') {
        modeTotals.offline += amount;
      } else if (t.payment_method === 'upi') {
        modeTotals.upi += amount;
      } else {
        modeTotals.others += amount;
      }
    });

  return {
    payment_links: {
      generated: inRange.length,
      converted: inRange.filter((t) => t.status === 'success').length
    },
    payment_modes: modeTotals
  };
};

export const getPaymentVolumeMock = (branchId?: string) => {
  const data = [];

  for (let i = 6; i >= 0; i -= 1) {
    const dayStart = new Date();
    dayStart.setDate(dayStart.getDate() - i);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    const dayTransactions = filterTransactionsInRange(
      branchId,
      dayStart,
      dayEnd,
      'due_at'
    ).filter((t) => t.status === 'success' || t.status === 'processing');

    const amountOf = (t: MockTransaction) =>
      Number(t.total_paid_amount) || Number(t.amount);

    data.push({
      day: dayStart.toISOString(),
      online: dayTransactions
        .filter((t) => t.payment_mode === 'automatic')
        .reduce((sum, t) => sum + amountOf(t), 0),
      offline_upi: dayTransactions
        .filter(
          (t) => t.payment_mode !== 'automatic' && t.payment_method === 'upi'
        )
        .reduce((sum, t) => sum + amountOf(t), 0),
      offline_bank: dayTransactions
        .filter((t) => t.payment_method === 'net_banking')
        .reduce((sum, t) => sum + amountOf(t), 0),
      offline_cash: dayTransactions
        .filter((t) => t.payment_method === 'offline')
        .reduce((sum, t) => sum + amountOf(t), 0)
    });
  }

  return { data };
};
